import React, { useState, useMemo } from "react";
import {
  Box,
  Input,
  VStack,
  HStack,
  Image,
  Text,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Flex,
  Link,
  Badge,
  Icon,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  chakra,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { SearchIcon } from "@chakra-ui/icons";
import {
  Play,
  Calendar,
  Clock,
  Sparkles,
  Radio,
  ChevronRight,
} from "lucide-react";
import Loading from "../../../components/Loading";
import { useTranslation } from "react-i18next";
import { useAlbumList, Album } from "../../../api/musics/musicsApi";

const MotionBox = chakra(motion.div);

const getImageUrl = (path: string | null) => {
  if (!path) return "/image/logo/logo.svg";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return path;
};

// Calculate relative days based on user's browser local device clock
const getRelativeDays = (dateStr: string | null | undefined): number | null => {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);

  // Concert date at midnight in user's local device timezone
  const concertDate = new Date(year, month, day);

  // Today at midnight in user's local device timezone
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const diffTime = today.getTime() - concertDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays >= 0 ? diffDays : 0;
};

const Music = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language ? i18n.language.split("-")[0] : "ko";
  const { data: albums, isLoading } = useAlbumList(lang === "ko" ? "ko" : "en");

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Theme Colors
  const cardBg = useColorModeValue("white", "gray.900");
  const trackBg = useColorModeValue("brand.purpleSoft", "whiteAlpha.50");
  const trackHoverBg = useColorModeValue("rgba(142, 41, 36, 0.12)", "whiteAlpha.100");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const textColorSec = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("brand.border", "whiteAlpha.100");
  const inputBg = useColorModeValue("white", "gray.800");

  const handleAlbumClick = (album: Album) => {
    setSelectedAlbum(album);
    onOpen();
  };

  // Filter albums or tracks based on search query
  const filteredAlbums = useMemo(() => {
    if (!albums) return [];
    if (!searchQuery.trim()) return albums;

    const query = searchQuery.toLowerCase().trim();
    return albums.filter((album) => {
      const matchAlbumName = album.name.toLowerCase().includes(query);
      const matchTrackName = album.tracks.some((track) =>
        track.name.toLowerCase().includes(query)
      );
      return matchAlbumName || matchTrackName;
    });
  }, [albums, searchQuery]);

  if (isLoading) return <Loading type="music" />;

  return (
    <Box width="100%" pt={2} pb={{ base: "100px", md: "120px" }}>
      {/* Sleek Search Bar */}
      <MotionBox
        maxW="420px"
        mx="auto"
        mb={5}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 } as any}
      >
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="brand.main" w="13px" h="13px" />
          </InputLeftElement>
          <Input
            placeholder={t("music_search_placeholder_album_track")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg={inputBg}
            borderColor={borderColor}
            borderRadius="full"
            boxShadow="0 2px 6px rgba(0,0,0,0.02)"
            _focus={{
              borderColor: "brand.main",
              boxShadow: "0 0 0 1px var(--chakra-colors-brand-main, #8e2924)",
            }}
            fontSize="xs"
            fontWeight="bold"
            h="34px"
          />
        </InputGroup>
      </MotionBox>

      {/* Compact Album Cards Grid View */}
      {filteredAlbums.length === 0 ? (
        <Flex
          justify="center"
          align="center"
          p={8}
          bg={cardBg}
          borderRadius="xl"
          border="1px dashed"
          borderColor={borderColor}
        >
          <Text fontSize="xs" color={textColorSec} fontWeight="bold">
            {t("no_matching_albums")}
          </Text>
        </Flex>
      ) : (
        <Grid
          templateColumns={{
            base: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            md: "repeat(4, 1fr)",
            lg: "repeat(5, 1fr)",
          }}
          gap={3.5}
        >
          {filteredAlbums.map((album, albumIdx) => (
            <MotionBox
              key={album.id}
              bg={cardBg}
              borderRadius="xl"
              border="1px solid"
              borderColor={borderColor}
              boxShadow="sm"
              overflow="hidden"
              cursor="pointer"
              onClick={() => handleAlbumClick(album)}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: albumIdx * 0.025 } as any}
              whileHover={{ y: -3, boxShadow: "0 6px 16px rgba(142, 41, 36, 0.15)" }}
              display="flex"
              flexDirection="column"
              role="group"
            >
              {/* Album Cover Container */}
              <Box position="relative" w="100%" pt="100%" overflow="hidden" bg="gray.100">
                <Image
                  src={getImageUrl(album.cover)}
                  alt={album.name}
                  position="absolute"
                  top={0}
                  left={0}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  transition="transform 0.25s ease"
                  _groupHover={{ transform: "scale(1.05)" }}
                  fallbackSrc="/image/logo/logo.svg"
                />
                <Badge
                  position="absolute"
                  top="6px"
                  right="6px"
                  bg="brand.main"
                  color="white"
                  borderRadius="full"
                  px={1.5}
                  py={0.2}
                  fontSize="9px"
                  fontWeight="bold"
                >
                  {album.tracks.length}{t("tracks_count_unit")}
                </Badge>
              </Box>

              {/* Compact Album Info Details */}
              <VStack p={2.5} align="start" spacing={0.5} flex={1} justify="space-between">
                <Box w="100%">
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color={textColor}
                    noOfLines={1}
                    lineHeight="tight"
                    _groupHover={{ color: "brand.main" }}
                    transition="color 0.2s"
                  >
                    {album.name}
                  </Text>
                  <HStack spacing={1} color={textColorSec} fontSize="10px" mt={0.5}>
                    <Icon as={Calendar} w="10px" h="10px" />
                    <Text>{album.release_date}</Text>
                  </HStack>
                </Box>

                <Flex
                  w="100%"
                  mt={1.5}
                  pt={1.5}
                  borderTop="1px solid"
                  borderColor={borderColor}
                  align="center"
                  justify="space-between"
                  color="brand.main"
                  fontSize="10px"
                  fontWeight="bold"
                >
                  <Text>{t("view_tracklist")}</Text>
                  <Icon as={ChevronRight} w="11px" h="11px" />
                </Flex>
              </VStack>
            </MotionBox>
          ))}
        </Grid>
      )}

      {/* Ultra Compact Album Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(3px)" bg="blackAlpha.400" />
        <ModalContent borderRadius="xl" overflow="hidden" bg={cardBg} boxShadow="lg" maxW={{ base: "90vw", sm: "420px" }}>
          {selectedAlbum && (
            <>
              <ModalHeader bg="brand.purpleSoft" p={3}>
                <Flex align="center" gap={2.5}>
                  <Image
                    src={getImageUrl(selectedAlbum.cover)}
                    alt={selectedAlbum.name}
                    boxSize="44px"
                    borderRadius="lg"
                    objectFit="cover"
                    boxShadow="2xs"
                    fallbackSrc="/image/logo/logo.svg"
                  />
                  <Box flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="bold" color={textColor} lineHeight="tight" noOfLines={1}>
                      {selectedAlbum.name}
                    </Text>
                    <HStack spacing={1.5} fontSize="10px" color={textColorSec} mt={0.5}>
                      <HStack spacing={0.5}>
                        <Icon as={Calendar} w="10px" h="10px" />
                        <Text>{t("release_date_label")}: {selectedAlbum.release_date}</Text>
                      </HStack>
                      <Text>·</Text>
                      <Text>{selectedAlbum.tracks.length}{t("tracks_count_unit")}</Text>
                    </HStack>
                  </Box>
                </Flex>
              </ModalHeader>
              <ModalCloseButton mt={1} mr={1} size="xs" borderRadius="full" />
              <ModalBody p={3}>
                <VStack spacing={1.5} align="stretch">
                  {selectedAlbum.tracks.map((track, idx) => {
                    const lastPerf = track.last_performed;
                    const hasPerf = Boolean(lastPerf && lastPerf.date);
                    const daysAgo = getRelativeDays(lastPerf?.date);

                    return (
                      <Box
                        key={track.id || idx}
                        py={1.5}
                        px={2.5}
                        bg={trackBg}
                        borderRadius="lg"
                        border="1px solid"
                        borderColor={borderColor}
                        transition="all 0.15s ease"
                        _hover={{ bg: trackHoverBg }}
                      >
                        <Flex align="center" justify="space-between">
                          <HStack spacing={2} minW={0} flex={1}>
                            <Flex
                              w="18px"
                              h="18px"
                              borderRadius="full"
                              bg="brand.main"
                              color="white"
                              align="center"
                              justify="center"
                              fontSize="9px"
                              fontWeight="bold"
                              flexShrink={0}
                            >
                              {track.play_order || idx + 1}
                            </Flex>
                            <Text fontSize="xs" fontWeight="bold" color={textColor} noOfLines={1}>
                              {track.name}
                            </Text>
                          </HStack>

                          {track.youtube_url && (
                            <IconButton
                              as={Link}
                              href={track.youtube_url}
                              isExternal
                              aria-label={t("listen")}
                              icon={<Icon as={Play} w="8px" h="8px" fill="white" color="white" ml="1px" />}
                              size="xs"
                              w="20px"
                              h="20px"
                              minW="20px"
                              bg="red.500"
                              color="white"
                              borderRadius="full"
                              _hover={{
                                bg: "red.600",
                                boxShadow: "0 2px 6px rgba(229, 62, 62, 0.4)",
                                transform: "translateY(-1px)",
                              }}
                              _active={{
                                bg: "red.700",
                                transform: "translateY(0)",
                              }}
                              transition="all 0.2s"
                              flexShrink={0}
                              ml={1.5}
                            />
                          )}
                        </Flex>

                        {/* Performance Info Sub-Box */}
                        <VStack align="start" spacing={1} mt={1} pt={1} borderTop="1px dashed" borderColor={borderColor} w="100%">
                          {hasPerf ? (
                            <>
                              {/* 1st Line: Concert Title */}
                              {lastPerf?.concert_name && (
                                <HStack spacing={1.5} color="brand.main" w="100%" align="start">
                                  <Icon as={Radio} w="10px" h="10px" flexShrink={0} mt="3px" color="brand.main" />
                                  <Text fontSize="11px" fontWeight="bold" color={textColor} lineHeight="1.3">
                                    {lastPerf.concert_name}
                                  </Text>
                                </HStack>
                              )}

                              {/* 2nd Line: Date & Days Ago */}
                              <HStack spacing={1.5} w="100%" align="center">
                                <Icon as={Calendar} w="10px" h="10px" flexShrink={0} color="brand.main" />
                                <HStack spacing={3} align="center">
                                  <Text fontSize="10px" fontWeight="medium" color="brand.main">
                                    {lastPerf?.formatted_date}
                                  </Text>

                                  {daysAgo !== null && daysAgo !== undefined && (
                                    <HStack spacing={1} color="pink.500">
                                      <Icon as={Clock} w="10px" h="10px" flexShrink={0} />
                                      <Text fontSize="10px" fontWeight="bold">
                                        {daysAgo === 0 ? t("today_concert") : `${daysAgo}${t("days_ago_suffix")}`}
                                      </Text>
                                    </HStack>
                                  )}
                                </HStack>
                              </HStack>
                            </>
                          ) : (
                            <HStack spacing={1} color="gray.500">
                              <Icon as={Sparkles} w="10px" h="10px" color="amber.500" flexShrink={0} />
                              <Text fontSize="10px" fontWeight="semibold">
                                {t("no_live_performance_record")}
                              </Text>
                            </HStack>
                          )}
                        </VStack>
                      </Box>
                    );
                  })}
                </VStack>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Music;
